import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import request from '../../utils/request';
import PageLayout from '../layout/PageLayout';
import { getAllHosts } from '../../store/thunks/HostActions';
import { getAllDrivers } from '../../store/thunks/driverActions';
import DashboardFilterBar from './DashboardFilterBar';
import KpiStrip from './KpiStrip';
import KpiRail from './KpiRail';
import DashboardCanvas from './DashboardCanvas';
import InsightsPanel from './InsightsPanel';
import RecordsSheet from './RecordsSheet';
import AnalyticsTabs from './AnalyticsTabs';
import DashboardDrawer from './DashboardDrawer';
import { CardSkeleton, ErrorState } from './states';
import { acquisitionWindows, formatRangeLabel, resolveRange } from './dateRange';
import {
  PMS_LABELS,
  aggregateOrdersByHost,
  countActivePropertiesFromOrders,
  enrichDriver,
  enrichHost,
  filterHostsByState,
  fmtMoney,
  fmtNumber,
  getOrderRevenue,
  newlyAcquiredDrivers,
  newlyOnboardedHosts,
  pmsDistribution,
} from './derive';

const DEFAULT_FILTERS = {
  range: '30d',
  customStart: null,
  customEnd: null,
  pms: 'all',
  hostType: 'all',
  hostQuery: '',
  propertySource: 'all',
  orderStatus: 'all',
};

export default function DashboardOverview() {
  const dispatch = useDispatch();
  const { hostList, loading: hostsLoading, error: hostsError } = useSelector((s) => s.allHosts);
  const { driverList, loading: driversLoading } = useSelector((s) => s.allDrivers);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertiesAvailable, setPropertiesAvailable] = useState(false);
  const [drawer, setDrawer] = useState({ kind: null, payload: null });
  const [activeView, setActiveView] = useState('map');
  const [recordsView, setRecordsView] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const range = useMemo(
    () => resolveRange(filters.range, filters.customStart, filters.customEnd),
    [filters.range, filters.customStart, filters.customEnd]
  );

  const loadHosts = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      try {
        await dispatch(getAllHosts({ limit: 1000, status: 'all' })).unwrap();
      } catch (_e) {
        // error surfaces through redux state
      } finally {
        if (silent) setRefreshing(false);
      }
    },
    [dispatch]
  );

  const loadDrivers = useCallback(
    async () => {
      try {
        await dispatch(getAllDrivers({ limit: 1000, status: 'all' })).unwrap();
      } catch (_e) {
        // error surfaces through redux state
      }
    },
    [dispatch]
  );

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const sinceISO = range.start.toISOString();
      const { data } = await request.get('/orders', {
        params: {
          status: 'completed,paymentCompleted,picked',
          since: sinceISO,
          limit: 2000,
        },
      });
      const list = Array.isArray(data) ? data : [];
      list.forEach((o) => {
        o._revenue = getOrderRevenue(o);
      });
      setOrders(list);
    } catch (e) {
      setOrdersError(e?.response?.data?.error || 'Failed to load orders');
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [range.start]);

  const loadProperties = useCallback(async () => {
    // Dedicated admin endpoint isolated from `/properties` so dashboard
    // shape changes don't affect any other admin screen.
    try {
      const { data } = await request.get('/properties/dashboard-summary', {
        params: { since: range.start.toISOString(), limit: 5000 },
      });
      const list = Array.isArray(data) ? data : [];
      const hasCreatedAtField =
        Array.isArray(data) &&
        (list.length === 0 ||
          list.some((p) => p && Object.prototype.hasOwnProperty.call(p, 'createdAt')));
      setProperties(list);
      setPropertiesAvailable(hasCreatedAtField);
    } catch (_e) {
      setProperties([]);
      setPropertiesAvailable(false);
    }
  }, [range.start]);

  useEffect(() => {
    loadHosts();
    loadDrivers();
  }, [loadHosts, loadDrivers]);

  useEffect(() => {
    loadOrders();
    loadProperties();
  }, [loadOrders, loadProperties]);

  const enrichedHosts = useMemo(
    () => (Array.isArray(hostList) ? hostList.map(enrichHost) : []),
    [hostList]
  );

  const enrichedDrivers = useMemo(
    () => (Array.isArray(driverList) ? driverList.map(enrichDriver) : []),
    [driverList]
  );

  const recentNewDrivers = useMemo(
    () => newlyAcquiredDrivers(enrichedDrivers, range),
    [enrichedDrivers, range]
  );

  const driverAcquisitionWindows = useMemo(() => {
    const w = acquisitionWindows();
    return {
      wtd: enrichedDrivers.filter((d) => {
        const t = d?.createdAt ? new Date(d.createdAt).getTime() : 0;
        return t >= w.wtd.start.getTime() && t <= w.wtd.end.getTime();
      }).length,
      mtd: enrichedDrivers.filter((d) => {
        const t = d?.createdAt ? new Date(d.createdAt).getTime() : 0;
        return t >= w.mtd.start.getTime() && t <= w.mtd.end.getTime();
      }).length,
      ytd: enrichedDrivers.filter((d) => {
        const t = d?.createdAt ? new Date(d.createdAt).getTime() : 0;
        return t >= w.ytd.start.getTime() && t <= w.ytd.end.getTime();
      }).length,
      total: enrichedDrivers.length,
    };
  }, [enrichedDrivers]);

  const filteredHosts = useMemo(
    () => filterHostsByState(enrichedHosts, filters),
    [enrichedHosts, filters]
  );

  const hostByPropertyId = useMemo(() => {
    const m = new Map();
    properties.forEach((p) => {
      if (p?._id && p?.hostID) m.set(String(p._id), String(p.hostID));
    });
    return m;
  }, [properties]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (filters.orderStatus !== 'all' && o.status !== filters.orderStatus) return false;
      const t = o.createdAt ? new Date(o.createdAt).getTime() : 0;
      if (t < range.start.getTime() || t > range.end.getTime()) return false;
      if (
        filters.pms !== 'all' ||
        filters.hostType !== 'all' ||
        filters.hostQuery
      ) {
        const propId = String(o.propertyID || o?.property?._id || '');
        const hostId = hostByPropertyId.get(propId) || String(o?.property?.hostID || '');
        if (!hostId) return false;
        const found = filteredHosts.find((h) => String(h._id) === hostId);
        if (!found) return false;
      }
      return true;
    });
  }, [orders, filters, range, hostByPropertyId, filteredHosts]);

  const hostOrderStats = useMemo(
    () => aggregateOrdersByHost(filteredOrders, hostByPropertyId),
    [filteredOrders, hostByPropertyId]
  );

  const hostRowsForRecords = useMemo(
    () =>
      filteredHosts.map((h) => {
        const stats = hostOrderStats?.get(String(h._id)) || { orders: 0, revenue: 0, aov: 0 };
        return {
          ...h,
          orderCount: stats.orders,
          revenue: stats.revenue,
          aov: stats.aov,
        };
      }),
    [filteredHosts, hostOrderStats]
  );

  const propertiesByPms = useMemo(() => {
    const m = new Map();
    if (!propertiesAvailable) return m;
    properties.forEach((p) => {
      const k = p?.propertySource || 'unknown';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  }, [properties, propertiesAvailable]);

  const recentNewHosts = useMemo(
    () => newlyOnboardedHosts(enrichedHosts, range),
    [enrichedHosts, range]
  );

  const recentNewProperties = useMemo(() => {
    if (!propertiesAvailable) return [];
    return properties.filter((p) => {
      if (!p?.createdAt) return false;
      const t = new Date(p.createdAt).getTime();
      return t >= range.start.getTime() && t <= range.end.getTime();
    });
  }, [properties, propertiesAvailable, range]);

  const metrics = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((s, o) => s + (o._revenue || 0), 0);
    const aov = totalOrders ? totalRevenue / totalOrders : 0;
    const newHostsCount = recentNewHosts.length;
    const activeHosts = enrichedHosts.filter((h) => h.active).length;
    const activeProperties = countActivePropertiesFromOrders(filteredOrders);

    const pmsCounts = pmsDistribution(filteredHosts);
    let topPms = null;
    let topPmsCount = 0;
    pmsCounts.forEach((c, k) => {
      if (k === 'unknown') return;
      if (c > topPmsCount) {
        topPmsCount = c;
        topPms = k;
      }
    });

    const newProps = propertiesAvailable ? recentNewProperties.length : null;

    return [
      { id: 'newHosts', label: 'New hosts', value: fmtNumber(newHostsCount), sublabel: formatRangeLabel(filters.range, filters.customStart, filters.customEnd), accent: 'indigo', tab: 'growth', filter: { hostType: 'new' } },
      { id: 'newDrivers', label: 'New drivers', value: fmtNumber(recentNewDrivers.length), sublabel: `WTD ${fmtNumber(driverAcquisitionWindows.wtd)} · MTD ${fmtNumber(driverAcquisitionWindows.mtd)} · YTD ${fmtNumber(driverAcquisitionWindows.ytd)}`, accent: 'emerald', tab: 'drivers', hint: 'Driver signups acquired in selected range. Sublabel shows stable WTD/MTD/YTD windows.' },
      { id: 'newProps', label: 'New props', value: newProps == null ? '—' : fmtNumber(newProps), sublabel: propertiesAvailable ? 'in range' : 'awaiting backend §B', accent: newProps == null ? 'zinc' : 'sky', tab: 'properties' },
      { id: 'activeHosts', label: 'Active hosts', value: fmtNumber(activeHosts), sublabel: 'verified & onboarded', accent: 'emerald', tab: 'hosts' },
      { id: 'activeProps', label: 'Active props', value: fmtNumber(activeProperties), sublabel: '2+ orders in scope', accent: 'sky', tab: 'hosts' },
      { id: 'orders', label: 'Orders', value: fmtNumber(totalOrders), sublabel: 'in range', accent: 'zinc', tab: 'orders' },
      { id: 'aov', label: 'Avg order', value: fmtMoney(aov), sublabel: `${fmtMoney(totalRevenue)} volume`, accent: 'amber', tab: 'orders' },
      { id: 'topPms', label: 'Top PMS', value: topPms ? PMS_LABELS[topPms] : '—', sublabel: topPms ? `${fmtNumber(topPmsCount)} hosts` : 'no signal yet', accent: 'indigo', tab: 'pms', filter: topPms ? { pms: topPms } : null, hint: 'Click to scope filters to top PMS' },
    ];
  }, [
    filteredOrders,
    enrichedHosts,
    filteredHosts,
    recentNewHosts.length,
    recentNewProperties.length,
    propertiesAvailable,
    recentNewDrivers.length,
    driverAcquisitionWindows.wtd,
    driverAcquisitionWindows.mtd,
    driverAcquisitionWindows.ytd,
    filters.range,
    filters.customStart,
    filters.customEnd,
  ]);

  const onMetricSelect = useCallback((m) => {
    if (m.tab) setActiveView(m.tab);
    if (m.filter) setFilters((cur) => ({ ...cur, ...m.filter }));
  }, []);

  const onRefresh = useCallback(() => {
    loadHosts({ silent: true });
    loadDrivers();
    loadOrders();
    loadProperties();
  }, [loadHosts, loadDrivers, loadOrders, loadProperties]);

  const subtitle = useMemo(
    () => `${formatRangeLabel(filters.range, filters.customStart, filters.customEnd)} · ${fmtNumber(enrichedHosts.length)} hosts tracked`,
    [filters.range, filters.customStart, filters.customEnd, enrichedHosts.length]
  );

  const onChangeFilters = useCallback((patch) => {
    setFilters((cur) => ({ ...cur, ...patch }));
  }, []);

  const onResetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const openHostDrawer = useCallback((host) => setDrawer({ kind: 'host', payload: host }), []);
  const openPropertyDrawer = useCallback((property) => setDrawer({ kind: 'property', payload: property }), []);
  const openPmsDrawer = useCallback((pmsId) => setDrawer({ kind: 'pms', payload: pmsId }), []);
  const openOrderDrawer = useCallback((order) => setDrawer({ kind: 'order', payload: order }), []);
  const closeDrawer = useCallback(() => setDrawer({ kind: null, payload: null }), []);

  const openRecords = useCallback((view) => setRecordsView(view), []);
  const closeRecords = useCallback(() => setRecordsView(null), []);

  // Active KPI mirrors current view so the rail's accent stays in sync.
  const activeMetricId = useMemo(() => {
    const m = metrics.find((x) => x.tab === activeView);
    return m?.id;
  }, [metrics, activeView]);

  return (
    <PageLayout
      title="Platform Dashboard"
      subtitle={subtitle}
      showSearch={false}
      fullWidth
    >
      <div className="space-y-3">
        <DashboardFilterBar
          filters={filters}
          onChange={onChangeFilters}
          onReset={onResetFilters}
          onRefresh={onRefresh}
          refreshing={refreshing || hostsLoading || ordersLoading || driversLoading}
          hostCount={filteredHosts.length}
        />

        {hostsError && enrichedHosts.length === 0 ? (
          <ErrorState
            title="Could not load hosts"
            description="Refresh to retry."
            onRetry={() => loadHosts({ silent: true })}
          />
        ) : null}

        {/* Cockpit (≥lg). Single viewport, internal scroll inside each panel. */}
        <div
          className="hidden lg:grid lg:grid-cols-12 lg:gap-3"
          style={{ height: 'calc(100vh - 220px)', minHeight: 540 }}
        >
          <div className="lg:col-span-2 min-h-0">
            {hostsLoading && enrichedHosts.length === 0 ? (
              <CardSkeleton className="h-full" />
            ) : (
              <KpiRail metrics={metrics} activeId={activeMetricId} onSelect={onMetricSelect} />
            )}
          </div>
          <div className="lg:col-span-7 min-h-0">
            <DashboardCanvas
              activeView={activeView}
              onChangeView={setActiveView}
              onOpenRecords={openRecords}
              filters={filters}
              range={range}
              filteredHosts={filteredHosts}
              filteredOrders={filteredOrders}
              hostOrderStats={hostOrderStats}
              drivers={enrichedDrivers}
              driverWindows={driverAcquisitionWindows}
              properties={properties}
              propertiesAvailable={propertiesAvailable}
              propertiesByPms={propertiesByPms}
              onSelectHost={openHostDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
          <div className="lg:col-span-3 min-h-0">
            <InsightsPanel
              newHosts={recentNewHosts}
              newProperties={recentNewProperties}
              hostsLoading={hostsLoading}
              propertiesAvailable={propertiesAvailable}
              hostOrderStats={hostOrderStats}
              enrichedHosts={enrichedHosts}
              filteredHosts={filteredHosts}
              filteredOrders={filteredOrders}
              properties={properties}
              hostsError={hostsError}
              ordersError={ordersError}
              onSelectHost={openHostDrawer}
              onSelectProperty={openPropertyDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
        </div>

        {/* Vertical fallback (<lg) — preserves all functionality. */}
        <div className="lg:hidden space-y-3">
          {hostsLoading && enrichedHosts.length === 0 ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <KpiStrip metrics={metrics} onSelect={onMetricSelect} />
          )}

          {ordersError ? (
            <ErrorState
              title="Could not load orders"
              description={ordersError}
              onRetry={loadOrders}
            />
          ) : null}

          <AnalyticsTabs
            activeTab={activeView === 'map' ? 'growth' : activeView}
            onTabChange={setActiveView}
            range={range}
            hosts={enrichedHosts}
            filteredHosts={filteredHosts}
            orders={filteredOrders}
            properties={properties}
            propertiesAvailable={propertiesAvailable}
            hostOrderStats={hostOrderStats}
            drivers={enrichedDrivers}
            newDrivers={recentNewDrivers}
            driverWindows={driverAcquisitionWindows}
            propertiesByPms={propertiesByPms}
            onSelectHost={openHostDrawer}
            onSelectProperty={openPropertyDrawer}
            onSelectOrder={openOrderDrawer}
            onSelectPms={openPmsDrawer}
          />
        </div>
      </div>

      <DashboardDrawer
        open={Boolean(drawer.kind)}
        kind={drawer.kind}
        payload={drawer.payload}
        hosts={enrichedHosts}
        properties={properties}
        propertiesAvailable={propertiesAvailable}
        onOpenHost={openHostDrawer}
        onOpenProperty={openPropertyDrawer}
        onClose={closeDrawer}
      />

      <RecordsSheet
        open={Boolean(recordsView)}
        view={recordsView}
        onClose={closeRecords}
        hostRows={hostRowsForRecords}
        filteredOrders={filteredOrders}
        drivers={enrichedDrivers}
        properties={properties}
        filteredHosts={filteredHosts}
        propertiesByPms={propertiesByPms}
        onSelectHost={openHostDrawer}
        onSelectOrder={openOrderDrawer}
        onSelectProperty={openPropertyDrawer}
        onSelectPms={openPmsDrawer}
      />
    </PageLayout>
  );
}
